import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentGame } from '@/store/gameSlice';
import { updateGameState } from '@/store/gameSlice';
import { selectUser } from '@/store/authSlice';
import { apiClient } from '@/services/api';
import { useDebugEnabled } from '@/debug/debugPreference';
import type { AppDispatch } from '@/store';
import type { GameState } from '@/types';
import { phaseLabel } from '@/game/phases';
import './GameStatusPanel.css';

// Game phases in order (excluding lobby, which is pre-game)
// start_game is a special one-time phase that shows the spaceship landing animation
const GAME_PHASES: GameState['phase'][] = ['start_game', 'land_claim', 'development', 'production', 'auction'];

/**
 * GameStatusPanel - Displays game status information
 *
 * Shows the turn, phase, status, and timer.
 * Phase arrows appear only after Debug is turned on from the profile menu.
 */
export default function GameStatusPanel() {
  const game = useSelector(selectCurrentGame);
  const user = useSelector(selectUser);
  const dispatch = useDispatch<AppDispatch>();
  const debugMode = useDebugEnabled();
  const [isChangingPhase, setIsChangingPhase] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState<string>('');

  // Listen for phase changes (from WebSocket or other updates)
  // Clear the waiting spinner when phase actually changes
  useEffect(() => {
    if (isChangingPhase && waitingMessage) {
      // If we were waiting for a phase change and the phase changed, clear spinner
      // This handles WebSocket updates
      const timeoutId = setTimeout(() => {
        if (isChangingPhase) {
          console.log('✅ Phase changed detected, clearing spinner');
          setWaitingMessage('');
          setIsChangingPhase(false);
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [game?.phase, game?.updatedAt]); // Watch for phase or updatedAt changes

  if (!game) {
    return null;
  }

  // Phase navigation logic
  // In lobby, can only go forward to start_game (first game phase)
  // In game phases, can navigate both directions
  const isInLobby = game.phase === 'lobby';
  const currentPhaseIndex = isInLobby ? -1 : GAME_PHASES.indexOf(game.phase);
  const canGoToPreviousPhase = debugMode && !isInLobby && currentPhaseIndex > 0;
  const canGoToNextPhase = debugMode && (isInLobby || currentPhaseIndex < GAME_PHASES.length - 1);

  const handlePreviousPhase = async () => {
    if (!canGoToPreviousPhase || isChangingPhase) return;
    
    setIsChangingPhase(true);
    const previousPhase = GAME_PHASES[currentPhaseIndex - 1];
    
    // In debug mode, just update locally (no server sync for backwards navigation)
    const updatedGame: GameState = {
      ...game,
      phase: previousPhase,
      updatedAt: new Date().toISOString(),
    };
    
    dispatch(updateGameState(updatedGame));
    setTimeout(() => setIsChangingPhase(false), 300);
  };

  const handleNextPhase = async () => {
    if (!canGoToNextPhase || isChangingPhase || !game || !user) return;
    
    setIsChangingPhase(true);
    setWaitingMessage('Initiating phase transition...');
    
    // Determine the next phase
    const nextPhase = isInLobby ? GAME_PHASES[0] : GAME_PHASES[currentPhaseIndex + 1];
    
    // Call phase_complete API to notify server and wait for all players
    // This is the standard multiplayer flow, even in debug mode
    try {
      console.log(`🎮 Completing phase "${game.phase}", calling phase_complete...`);
      setWaitingMessage('Waiting for all players...');
      const data = await apiClient.phaseComplete({
        gameId: game.gameId,
        phase: game.phase,
        etag: game.etag,
      });
      console.log('✅ Phase complete response:', data);
      if (data.allPlayersReady && data.gameState) {
          // Server has transitioned the phase, update local state
          setWaitingMessage('All players ready! Transitioning...');
          dispatch(updateGameState(data.gameState));
          
          setTimeout(() => {
            setWaitingMessage('');
            setIsChangingPhase(false);
          }, 500);
        } else if (data.allPlayersReady && data.newPhase) {
          // Server transitioned but didn't send full state (will come via WebSocket)
          setWaitingMessage('Phase transition complete!');
          console.log(`🎉 All players ready! Server transitioned to ${data.newPhase}`);
          console.log('📡 Waiting for WebSocket update with new game state...');
          
          // Keep spinner visible until WebSocket update arrives
          // The game state will be updated via WebSocket listener in App.tsx or gameSlice
          // For now, clear after a timeout to prevent infinite spinner
          setTimeout(() => {
            setWaitingMessage('');
            setIsChangingPhase(false);
          }, 3000);
        } else {
          // Still waiting for other players
          const waitingCount = (data.totalPlayers || 1) - (data.completedPlayers?.length || 0);
          setWaitingMessage(`Waiting for ${waitingCount} player(s)...`);
          console.log(`⏳ Waiting for other players: ${data.completedPlayers?.length}/${data.totalPlayers}`);
          
          // Keep spinner visible - in production, WebSocket will notify when all ready
          // For testing, add a timeout to prevent infinite spinner
          setTimeout(() => {
            setWaitingMessage('Still waiting for other players...');
          }, 5000);
        }
    } catch (error) {
      console.error('❌ Error calling phase_complete:', error);
      
      // Fallback: Update locally (debug mode only)
      if (debugMode) {
        setWaitingMessage('Network error, updating locally...');
        
        const updatedGame: GameState = {
          ...game,
          phase: nextPhase,
          updatedAt: new Date().toISOString(),
        };
        
        dispatch(updateGameState(updatedGame));
        
        setTimeout(() => {
          setWaitingMessage('');
          setIsChangingPhase(false);
        }, 1000);
      } else {
        setWaitingMessage('Network error');
        setTimeout(() => {
          setWaitingMessage('');
          setIsChangingPhase(false);
        }, 2000);
      }
    }
  };

  const formatPhaseName = (phase: GameState['phase']): string => phaseLabel(phase);

  // Format game status for display
  const formatStatus = (status: GameState['status']): string => {
    const statusNames: Record<GameState['status'], string> = {
      lobby: 'Lobby',
      active: 'Active',
      completed: 'Completed',
      paused: 'Paused',
    };
    return statusNames[status] || status;
  };

  return (
    <div className="game-status-panel">
      <h3 className="game-status-title">GAME STATUS</h3>
      
      {isChangingPhase && waitingMessage && (
        <div className="phase-waiting-overlay">
          <div className="phase-waiting-spinner"></div>
          <div className="phase-waiting-message">{waitingMessage}</div>
        </div>
      )}
      
      <div className="game-status-content">
        <div className="status-item">
          <span className="status-label">Turn:</span>
          <span className="status-value">
            {game.currentTurn} / {game.maxTurns}
          </span>
        </div>
        
        <div className="status-item">
          <span className="status-label">Phase:</span>
          <div className="phase-control">
            {debugMode && (
              <button
                className="phase-nav-button phase-nav-prev"
                onClick={handlePreviousPhase}
                disabled={!canGoToPreviousPhase || isChangingPhase}
                title="Previous Phase (Debug Mode)"
                aria-label="Previous Phase"
              >
                ←
              </button>
            )}
            <span className="status-value phase-name">{formatPhaseName(game.phase)}</span>
            {debugMode && (
              <button
                className="phase-nav-button phase-nav-next"
                onClick={handleNextPhase}
                disabled={!canGoToNextPhase || isChangingPhase}
                title="Next Phase (Debug Mode)"
                aria-label="Next Phase"
              >
                {isChangingPhase ? '⏳' : '→'}
              </button>
            )}
          </div>
        </div>
        
        <div className="status-item">
          <span className="status-label">Status:</span>
          <span className="status-value">{formatStatus(game.status)}</span>
        </div>
        
        {debugMode && (
          <div className="debug-indicator">
            <span className="debug-badge">DEBUG MODE</span>
            <span className="debug-hint">Manual phase navigation enabled</span>
          </div>
        )}
      </div>
    </div>
  );
}

